/* eslint-disable @typescript-eslint/no-explicit-any */
import { htmlSpecialChars } from 'src/util/url';
import { toArray } from 'src/util/converter';
import { getBoundingRectangle, calculateUnrotatedWidthAndHeight } from 'src/util/math';
import idGeneratorAtor from 'src/util/idGeneratorAtor';
import { COMPOSITE, DOCUMENT, INSTRUCTION, MASK } from 'src/pages/editor/components/layer/layerTypes';
import {
    ENGRAVING_CHANNEL,
    LETTERPRESS_CHANNEL,
    LETTERPRESS_MODE_SOFTLIGHT,
    LETTERPRESS_MODES,
    LetterPressMode,
    NORMAL,
} from 'src/config/constants';
import { CYLINDER_WARP, DOCUMENT_ID, SMOOTH_WARP, TEXTURE_WARP } from 'src/models/documentWarps';
import { deflateRaw } from 'pako';
import { Document, ENGRAVING_COLOR } from 'src/pages/editor/components/documents/slice';
import { SurfaceType } from 'src/types/surface';
import {
    AutoMaskInstructionType,
    DocumentXmlObject,
    EngravingImageType,
    XMLCompositeType,
    XMLImageType,
    XMLTree_CompositeComposite,
    XMLTree_DIP,
} from 'src/types/xml';
import { PointType } from 'src/types';
import { TransformTypeField } from 'src/types/transforms';
import { FinishData } from 'src/services/materialClient';

const idGenerator = idGeneratorAtor(DOCUMENT_ID);

const BACKGROUND_DOC_PLACEHOLDER = (color: string) => ({
    documentId: 'demoEllipseDocument',
    version: '2',
    deleted: false,
    document: {
        panels: [
            {
                id: 'surface1',
                name: 'Item',
                width: '1mm',
                height: '1mm',
                shapes: [
                    {
                        type: 'rectangle',
                        zIndex: 0,
                        position: {
                            x: '0mm',
                            y: '0mm',
                            width: '1mm',
                            height: '1mm',
                        },
                        id: 'shape1',
                        color: `rgb(${color})`,
                        itemType: 'shape',
                    },
                ],
                decorationTechnology: 'offsetOrDigital',
            },
        ],
    },
    fontRepositoryUrl:
        'https://fonts.documents.cimpress.io/v1/repositories/aff15d65-e10f-492d-b8ea-cfd454c93c3f/published',
});

/* eslint-disable no-underscore-dangle */
export function buildDocumentObject(doc: Document) {
    const { page } = doc;

    let size;
    if (doc.width || doc.height) {
        size = `${doc.width},${doc.height}`;
    } else {
        const { points, type } = (doc.transforms || [])[0];
        let { width, height } = calculateUnrotatedWidthAndHeight(points);
        const sourceBox = getBoundingRectangle(points.map((point) => ({ x: point.sourceX, y: point.sourceY })));
        if (sourceBox.width !== 1 || sourceBox.height !== 1) {
            width /= sourceBox.width;
            height /= sourceBox.height;
        }

        if (type === SMOOTH_WARP) {
            const length = Math.ceil(Math.max(width, height));
            size = `${length},${length}`;
        } else {
            size = `${Math.ceil(width)},${Math.ceil(height)}`;
        }
    }

    const docData: DocumentXmlObject = {
        size,
        name: doc.id,
        type: DOCUMENT,
        mode: doc.mode,
        page,
        offset: 0,
    };

    if (
        doc.colorProfile !== undefined &&
        doc.colorProfile !== null &&
        doc.colorProfile !== '' &&
        doc.colorProfile !== 'RGB'
    ) {
        docData.colorProfile = doc.colorProfile;
    }
    return docData;
}

export function buildMaskObject(doc: Document, size: string) {
    const { id, maskName, maskUrl } = doc;
    if (!maskUrl) {
        return undefined;
    }

    return {
        size,
        src: htmlSpecialChars(maskUrl),
        name: maskName || `${id}-Mask`,
        type: MASK,
        mode: MASK,
        depth: 0,
    };
}

function buildInstructionsContent(docSize: PointType, surface: SurfaceType) {
    const { trimAreas, fullBleedAreas } = surface;

    const Contours = trimAreas.map((trimArea, i) => {
        const widthScaler = docSize.x / fullBleedAreas[i].width;
        const heightScaler = docSize.y / fullBleedAreas[i].height;
        const { pathPoints } = trimArea;

        // Always start in the middle of the document
        const StartPointX = trimArea.anchorX * widthScaler;
        const StartPointY = trimArea.anchorY * heightScaler;

        const calculateSegments = pathPoints.map((pathPoint) => {
            const isCubic = pathPoint.firstControlPointX !== undefined && pathPoint.secondControlPointX !== undefined;
            let extra = {};
            const type = isCubic ? 'CubicTo' : 'LineTo';
            if (isCubic) {
                extra = {
                    ControlPoint1: {
                        X: pathPoint.firstControlPointX * widthScaler,
                        Y: pathPoint.firstControlPointY * heightScaler,
                    },
                    ControlPoint2: {
                        X: pathPoint.secondControlPointX * widthScaler,
                        Y: pathPoint.secondControlPointY * heightScaler,
                    },
                };
            }
            return {
                Type: type,
                ...extra,
                EndPoint: {
                    X: pathPoint.endPointX * widthScaler,
                    Y: pathPoint.endPointY * heightScaler,
                },
            };
        });

        return {
            Closed: true,
            StartPoint: {
                X: StartPointX,
                Y: StartPointY,
            },
            ContourSegments: calculateSegments,
        };
    });
    return JSON.stringify([
        {
            Type: 'Path',
            Fill: {
                Color: {
                    R: 0,
                    G: 0,
                    B: 0,
                    A: 255,
                },
            },
            PathContours: {
                Contours,
            },
        },
    ]);
}

export function buildAutomask(doc: DocumentXmlObject, surface: SurfaceType | undefined): AutoMaskInstructionType {
    const sizeValues = doc.size.split(',');
    const docSize = {
        x: parseInt(sizeValues[0]),
        y: parseInt(sizeValues[1]),
    };
    const content = surface ? buildInstructionsContent(docSize, surface) : undefined;

    return {
        size: doc.size,
        name: `${doc.name}-Mask`,
        mode: MASK,
        depth: 0,
        type: INSTRUCTION,
        content,
    };
}

export function buildEngravingImage(size: string, color = ENGRAVING_COLOR): EngravingImageType {
    const [height, width] = size.split(',');
    const content = JSON.stringify(BACKGROUND_DOC_PLACEHOLDER(color));
    const deflatedContent = deflateRaw(content, { to: 'string' });
    const encodedContent = encodeURIComponent(btoa(deflatedContent));
    const instructionsUri = `https://instructions.documents.cimpress.io/v3/instructions:preview?documentUri=${encodedContent}`;

    return {
        size,
        src: htmlSpecialChars(
            `https://rendering.documents.cimpress.io/v1/cse/preview?width=${width}&height=${height}&` +
                `format=webp&instructions_uri=${encodeURIComponent(instructionsUri)}`,
        ),
        name: 'Silver-Engraving',
        mode: NORMAL,
        depth: 1,
    };
}

export function buildFinishComposite(props: {
    finish: FinishData;
    doc: DocumentXmlObject;
    depth: number;
    size?: string;
}): XMLCompositeType {
    const { finish, doc, depth, size } = props;

    const docXMLObj: DocumentXmlObject = {
        ...doc,
        name: `${finish.name} Channel Mask`,
        mode: MASK,
        channel: finish.scene.channel,
        depth: 0,
    };

    const imageXMLObj: XMLImageType = {
        size: size ?? doc.size,
        name: `Tiled Finish Base - ${finish.name}`,
        mode: NORMAL,
        depth: 1,
        src: finish.scene.baseTexture.url,
        tileScale: 0.0,
        type: 'Image',
    };

    return {
        size: size ?? doc.size,
        name: `Document Finish Composite - ${finish.name}`,
        depth,
        xform: `${doc.name} ${finish.name} Transform`,
        children: [imageXMLObj, docXMLObj],
        type: COMPOSITE,
        mode: NORMAL,
    };
}

const hasMask = (composite: XMLTree_CompositeComposite) => {
    let founded = false;
    Object.keys(composite).forEach((key) => {
        if (founded) {
            return;
        }

        const ele = composite[key];
        if (key === 'Image' && ele._mode === MASK) {
            founded = true;
        } else if (typeof ele === 'object') {
            founded = hasMask(ele);
        }
    });

    return founded;
};

const hasAutomask = (composite: XMLTree_CompositeComposite) => {
    let founded = false;
    Object.keys(composite).forEach((key) => {
        if (founded) {
            return;
        }

        const ele = composite[key];
        if (key === 'Instructions' && ele._mode === MASK) {
            founded = true;
        }
    });

    return founded;
};

const getFinishes = (
    composite: XMLTree_CompositeComposite,
    finishes: { finish: FinishData; xform: string }[] = [],
    finishChannels: string[],
    finishdata: Record<string, FinishData>,
) => {
    if (Array.isArray(composite)) {
        composite.forEach((comp) => getFinishes(comp, finishes, finishChannels, finishdata));
    } else if (composite.Document?._channel && finishChannels.includes(composite.Document._channel)) {
        finishes.push({
            finish: finishdata[composite.Document._channel],
            xform: composite._xform ?? composite.Document._xform,
        });
    } else if (composite.Composite) {
        getFinishes(composite.Composite, finishes, finishChannels, finishdata);
    }

    return;
};

const sortDocuments = (a: XMLTree_CompositeComposite, b: XMLTree_CompositeComposite): number =>
    a._name < b._name ? -1 : a._name > b._name ? 1 : 0;

export function xmlToDocumentObjects(dip: XMLTree_DIP, finishList: FinishData[]) {
    const transformIdMap: Record<string, any> = {};
    // keep track of finish transforms to be ignored when parsing transforms for documents, as they are not actual transforms applied
    // directly to the document but used to lay the finish over the document
    const finishTransformNames: string[] = [];
    const addWarp = (name: string, warpType: TransformTypeField) =>
        toArray(dip.Transforms[name]).forEach(
            (transform: Record<string, any>) => (transformIdMap[transform._id] = warpType),
        ); // eslint-disable-line no-return-assign
    addWarp('RectangleWarp', 'RectangleWarp');
    addWarp('PerspectiveWarp', 'PerspectiveWarp');
    addWarp('TubeWarp', 'CylinderWarp');
    addWarp('PlacementWarp', 'CylinderWarp'); //TODO: Check why they are using cylinder for both tube and placement
    addWarp('SmoothWarp', 'SmoothWarp');
    addWarp('TextureWarp', 'TextureWarp');

    const composites: XMLTree_CompositeComposite[] = toArray(dip.Composite.Composite);
    const documents = composites
        .slice()
        .sort(sortDocuments) // The order matters to place documents with more depth underneath others.
        .map((composite) => {
            let doc: Document | undefined;
            let documentNode = composite.Document;
            let letterPressData: { mode: LetterPressMode; depth: number } | undefined;

            // Check for letterpress in nested composite wrapper (new structure)
            if (composite.Composite) {
                const childComposites = toArray(composite.Composite);
                const letterPressWrapper = childComposites.find(
                    (c: any) => c._name && c._name.includes('-Letterpress-wrapper'),
                );

                if (letterPressWrapper && letterPressWrapper.Document) {
                    const letterPressDoc = letterPressWrapper.Document;
                    const mode = letterPressDoc._mode as LetterPressMode;
                    letterPressData = {
                        mode: (LETTERPRESS_MODES.includes(mode as LetterPressMode)
                            ? mode
                            : LETTERPRESS_MODE_SOFTLIGHT) as LetterPressMode,
                        depth: parseInt(letterPressDoc._depth),
                    };
                    // Main document should be a sibling to the wrapper
                    // Keep the existing documentNode from composite.Document
                } else if (!documentNode) {
                    // Fallback for nested composites without letterpress
                    documentNode =
                        composite.Composite.Document ||
                        (composite.Composite.Composite && composite.Composite.Composite.Document);
                }
            }

            // Check if composite.Document is an array (multiple documents - legacy structure)
            if (Array.isArray(documentNode)) {
                // Look for letterpress document (should be first)
                const letterPressDoc = documentNode.find((d) => d._channel === LETTERPRESS_CHANNEL);
                if (letterPressDoc) {
                    const mode = letterPressDoc._mode as LetterPressMode;
                    letterPressData = {
                        mode: (LETTERPRESS_MODES.includes(mode as LetterPressMode)
                            ? mode
                            : LETTERPRESS_MODE_SOFTLIGHT) as LetterPressMode,
                        depth: parseInt(letterPressDoc._depth),
                    };
                    // Use the draw document (not the letterpress one)
                    documentNode = documentNode.find((d) => d._channel !== LETTERPRESS_CHANNEL);
                } else {
                    // Multiple documents but no letterpress, pick the one with mode='normal'
                    documentNode = documentNode.find((d) => d._mode === NORMAL) || documentNode[0];
                }
            }

            if (documentNode) {
                doc = {
                    mode: composite._mode || NORMAL,
                    warpType: transformIdMap[documentNode._xform],
                    page: parseInt(documentNode._page),
                    colorProfile: documentNode._colorProfile,
                    id: documentNode._name || idGenerator(),
                    transforms: [],
                };
                if (doc.warpType === CYLINDER_WARP || doc.warpType === TEXTURE_WARP) {
                    const size = documentNode._size.split(',');
                    Object.assign(doc, {
                        width: parseFloat(size[0]),
                        height: parseFloat(size[1]),
                    });
                }

                // Apply letter press data if found
                if (letterPressData) {
                    doc.letterPress = letterPressData;
                }

                if (documentNode._channel === ENGRAVING_CHANNEL) {
                    doc.isEngraving = true;
                }
                if (hasMask(composite) && composite.Image) {
                    doc.maskUrl = composite.Image._src;
                    doc.maskName = composite.Image._name;
                }
                if (hasAutomask(composite)) {
                    doc.automask = true;
                }

                const finishes: { finish: FinishData; xform: string }[] = [];
                const finishData = finishList.reduce(
                    (acc, finish) => {
                        acc[finish.scene.channel] = finish;
                        return acc;
                    },
                    {} as Record<string, FinishData>,
                );
                getFinishes(composite, finishes, Object.keys(finishData || {}), finishData);

                if (finishes.length > 0) {
                    finishTransformNames.push(...finishes.map((finish) => finish.xform));
                    doc.finishes = finishes.map(({ finish }) => finish);
                }
            }

            return doc;
        })
        .filter((doc) => doc !== undefined) as Document[];

    return { documents, finishTransformNames };
}
